import { Paperclip, Pencil, X } from "lucide-react";
import {
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";

import { Button, IconButton } from "@/components";
import { formatBytes } from "@/lib/datetime";
import { FOCUS, PRESS_FLAT } from "@/lib/interaction";
import { ENTER, RISE_IN } from "@/lib/motion";

/** 입력창이 이 높이를 넘으면 그 안에서 스크롤한다 */
const MAX_HEIGHT = 160;

/**
 * 첨부 합계 상한. Cloudflare가 한 통에 5 MiB까지만 받는다.
 * 서버도 같은 값으로 막지만, 다 올려보낸 뒤 거절당하는 것보다 여기서 막는 게 낫다.
 */
const MAX_TOTAL_BYTES = 4 * 1024 * 1024;

export type ComposerAttachment = {
  filename: string;
  mimeType: string;
  contentBase64: string;
  size: number;
};

/** 파일 하나를 base64로. FileReader는 `data:...;base64,` 접두사를 붙여준다. */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("파일을 읽지 못했어요"));
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.readAsDataURL(file);
  });
}

type ComposerProps = {
  /** 답장할 수 없는 방이면 입력창 대신 안내가 뜬다 (시안 04) */
  disabled?: boolean;
  disabledMessage?: string;
  placeholder?: string;
  /** 그냥 보내면 붙을 제목. 보통 앞 메일에 `Re:`를 얹은 것이다. */
  defaultSubject?: string;
  /**
   * 쓰다 만 글. **방마다 따로** 보관해야 해서 밖에서 들고 있다.
   *
   * 여기서 state로 들고 있으면 방을 옮겨도 같은 인스턴스가 살아남아 쓰던 글이
   * 그대로 따라간다. 김대리에게 쓰던 문장이 박팀장 방에 들어 있고, 그대로
   * Enter를 치면 엉뚱한 사람에게 나간다 — 메일이라 주워담을 수도 없다.
   */
  value: string;
  onValueChange: (value: string) => void;
  onSend: (
    body: string,
    attachments: ComposerAttachment[],
    /** 사용자가 직접 고쳐 쓴 제목. 안 고쳤으면 undefined. */
    subject?: string,
  ) => void;
  /** 첨부가 상한을 넘는 등, 사용자에게 알려야 할 일 */
  onNotice?: (message: string) => void;
};

export function Composer({
  disabled = false,
  // 못 보낸다는 사실만 알리면 앱이 막은 것처럼 읽힌다. 이유는 상대 쪽에 있다.
  disabledMessage = "답장을 받지 않는 주소예요. 보낼 곳이 없어요",
  // 방을 아는 쪽(MailPage)이 "누구에게"까지 채워 넘긴다. 이건 그때의 대비책이다.
  placeholder = "메시지 보내기",
  defaultSubject,
  value,
  onValueChange,
  onSend,
  onNotice,
}: ComposerProps) {
  const [files, setFiles] = useState<ComposerAttachment[]>([]);
  /** null이면 "앞 제목을 그대로 쓴다". 문자열이면 사용자가 고쳐 쓰는 중. */
  const [subject, setSubject] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * 쓴 만큼 칸이 자란다. 보관해둔 초안을 다시 넣을 때도 높이가 같이 돌아와야
   * 여러 줄짜리 초안이 한 줄 칸에 갇히지 않는다.
   *
   * **빈 칸일 때는 재지 않는다.** `scrollHeight`는 내용이 없으면 placeholder를
   * 기준으로 잡는데, 안내문이 길어 두 줄로 접히면 아무것도 안 썼는데 입력칸이
   * 두 줄 높이로 벌어진다. 쓸 내용이 없는데 자리를 차지할 이유가 없다.
   */
  useLayoutEffect(() => {
    const element = textareaRef.current;
    if (!element) return;

    element.style.height = "auto";
    if (value.length === 0) return;

    element.style.height = `${Math.min(element.scrollHeight, MAX_HEIGHT)}px`;
  }, [value]);

  // 훅은 전부 위에 둔다. 이 분기를 위로 올리면 disabled가 바뀌는 순간 훅 순서가 어긋난다.
  if (disabled) {
    return (
      <div className="px-4 pb-4 sm:px-8 sm:pb-6.5">
        <p className="text-muted-2 flex h-13 items-center justify-center rounded-[14px] bg-sunken text-sm">
          {disabledMessage}
        </p>
      </div>
    );
  }

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    onValueChange(event.target.value);
  }

  async function handlePick(event: ChangeEvent<HTMLInputElement>) {
    const picked = [...(event.target.files ?? [])];
    // 같은 파일을 다시 고를 수 있어야 한다. 값을 비우지 않으면 change가 안 뜬다.
    event.target.value = "";
    if (picked.length === 0) return;

    let total = files.reduce((sum, file) => sum + file.size, 0);
    const added: ComposerAttachment[] = [];

    for (const file of picked) {
      if (total + file.size > MAX_TOTAL_BYTES) {
        onNotice?.(
          `${file.name}은(는) 너무 커요. 첨부는 모두 합쳐 ${formatBytes(MAX_TOTAL_BYTES)}까지예요`,
        );
        continue;
      }

      try {
        added.push({
          filename: file.name,
          mimeType: file.type || "application/octet-stream",
          contentBase64: await readAsBase64(file),
          size: file.size,
        });
        total += file.size;
      } catch {
        onNotice?.(`${file.name}을(를) 읽지 못했어요`);
      }
    }

    if (added.length > 0) setFiles((current) => [...current, ...added]);
  }

  function submit() {
    const trimmed = value.trim();
    if (trimmed.length === 0) return;

    const edited = subject?.trim();
    onSend(trimmed, files, edited && edited.length > 0 ? edited : undefined);

    onValueChange("");
    setFiles([]);
    // 제목은 한 통에만 적용된다. 다음 메시지는 다시 앞 제목을 잇는다 —
    // 대화가 그 제목으로 굳어버리면 바꾼 사람만 알고 상대는 모른다.
    setSubject(null);

    const element = textareaRef.current;
    if (element) {
      element.style.height = "auto";
      element.focus();
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter로 보내고 Shift+Enter로 줄바꿈. 메일이지만 채팅처럼 쓰는 앱이므로
    // 메신저 관습을 따른다. 조합 중(한글 입력)에는 가로채지 않는다.
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) {
      return;
    }

    event.preventDefault();
    submit();
  }

  return (
    <div className="flex flex-col gap-2 px-4 pb-4 sm:px-8 sm:pb-6.5">
      {/*
        메일에는 제목이 있고, 답장에도 있다. 채팅처럼 쓰는 앱이라 평소에는
        앞 제목을 그대로 잇지만(`Re: …`), 이야기가 바뀌면 바꿀 수 있어야 한다.
        상대 메일함에 뜨는 것이 이 값이다.

        평소에는 한 줄로 조용히 보여주고, 누르면 고칠 수 있게 한다. 늘 입력칸으로
        두면 채팅 입력창이 둘로 보인다.
      */}
      {subject === null ? (
        /*
          이어받을 제목이 없으면 **빈 제목으로 열어준다.**

          말 걸기 전에 연 방에는 앞 메일이 없어서 `defaultSubject`가 비어 있다.
          예전에는 그때 이 줄을 통째로 안 그렸는데, 그러면 첫 메일의 제목을 정할
          길이 아예 없다 — 서버가 본문 앞 60자를 제목으로 삼으므로 상대 메일함에
          말 한마디가 제목으로 뜬다.
        */
        <button
          type="button"
          onClick={() => setSubject(defaultSubject ?? "")}
          className={`text-muted-2 hover:text-body active:text-ink group -mx-1.5 flex items-center gap-1.5 self-start rounded px-1.5 py-0.5 text-[13px] ${PRESS_FLAT} ${FOCUS}`}
        >
          <span className="text-muted-3 shrink-0">제목</span>
          {/* 아직 없으면 무엇을 하는 자리인지 흐리게 일러준다 */}
          <span
            className={`max-w-120 truncate ${defaultSubject ? "" : "text-muted-3"}`}
          >
            {defaultSubject || "붙이기"}
          </span>
          {/*
            연필은 **항상 보인다.** hover에서만 나타나면 마우스를 얹기 전까지
            고칠 수 있는 줄인지 알 수 없고, 마우스가 없는 사람에게는 영영
            나타나지 않는다. 평소엔 흐리게 두고 얹었을 때 또렷해진다.
          */}
          <Pencil className="text-muted-3 group-hover:text-body size-3 shrink-0 transition-colors" />
        </button>
      ) : (
        <div className={`flex items-center gap-2 ${ENTER}`}>
          <label htmlFor="composer-subject" className="text-muted-3 text-[13px]">
            제목
          </label>
          <input
            id="composer-subject"
            value={subject}
            autoFocus
            maxLength={200}
            onChange={(event) => setSubject(event.target.value)}
            onKeyDown={(event) => {
              if (event.nativeEvent.isComposing) return;
              // Enter는 본문으로 넘어가는 뜻이지 보내는 뜻이 아니다.
              if (event.key === "Enter") {
                event.preventDefault();
                textareaRef.current?.focus();
              }
              // 되돌리려면 Esc.
              if (event.key === "Escape") setSubject(null);
            }}
            className="bg-surface border-line-input text-ink focus:outline-ink min-w-0 flex-1 rounded-lg border px-3 py-1.5 text-[13px] outline-none focus:outline-2 focus:outline-offset-2"
          />
          <button
            type="button"
            onClick={() => setSubject(null)}
            className={`text-muted-2 hover:text-body active:text-ink shrink-0 rounded px-1 py-0.5 text-[13px] ${PRESS_FLAT} ${FOCUS}`}
          >
            되돌리기
          </button>
        </div>
      )}

      {files.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {files.map((file, index) => (
            <li
              key={`${file.filename}-${index}`}
              // 고른 파일이 툭 나타나면 눌린 결과인지 화면이 튄 것인지 모른다.
              className={`bg-chip text-body flex items-center gap-1.5 rounded-lg py-1 pr-1.5 pl-2.5 text-[13px] ${RISE_IN}`}
            >
              <Paperclip className="text-muted-2 size-3 shrink-0" />
              <span className="max-w-50 truncate">{file.filename}</span>
              <span className="text-muted-2 shrink-0 text-[11px]">
                {formatBytes(file.size)}
              </span>
              <button
                type="button"
                aria-label={`${file.filename} 빼기`}
                onClick={() =>
                  setFiles((current) =>
                    current.filter((_, position) => position !== index),
                  )
                }
                className={`text-muted-2 hover:text-ink active:text-body rounded ${PRESS_FLAT} ${FOCUS}`}
              >
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex items-end gap-2.5">
        {/*
          min-w-0가 없으면 이 칸이 제 내용만큼 자리를 차지하겠다고 버텨서, 옆에
          있는 보내기 단추가 화면 밖으로 밀려난다. flex 자식의 기본값이
          `min-width: auto`라 flex-1만으로는 줄어들지 않는다.
        */}
        <div className="bg-surface border-line-input focus-within:outline-ink flex min-h-13 min-w-0 flex-1 items-end gap-3.5 rounded-[14px] border px-3.5 py-2.5 focus-within:outline-2 focus-within:outline-offset-2 sm:px-4.5">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(event) => {
              void handlePick(event);
            }}
          />
          <IconButton
            label="파일 첨부"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="mb-0.5 -ml-1.5"
          >
            <Paperclip />
          </IconButton>

          <label htmlFor="composer-body" className="sr-only">
            메시지 입력
          </label>
          <textarea
            id="composer-body"
            ref={textareaRef}
            rows={1}
            value={value}
            placeholder={placeholder}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            // 안내문은 접지 않는다. 두 줄이 되면 그 두 번째 줄이 어중간하게
            // 잘려 보인다. (`text-overflow`로 …를 붙이는 건 `::placeholder`에
            // 먹지 않아서, 길이 자체는 roomView가 화면 폭에 맞춰 정한다.)
            className="text-ink placeholder:text-muted-3 mb-1.5 max-h-40 min-w-0 flex-1 resize-none bg-transparent text-[15px] leading-[1.55] outline-none placeholder:overflow-hidden placeholder:whitespace-nowrap"
          />
        </div>

        <Button size="lg" onClick={submit} disabled={value.trim().length === 0}>
          보내기
        </Button>
      </div>
    </div>
  );
}
